/**
 * Abstract class providing different Helper functions.
 * @abstract
 */
class Helper {
	/**
	 * Checks if given parameter is an object.
	 * @param {*} checkMe - Parameter to be checked.
	 * @returns {boolean} If parameter is an object.
	 * @static
	 */
	static isObject (checkMe) {
		return checkMe && checkMe.constructor.name === 'Object';
	}

	/**
	 * Clones an object if given param is object. Else the value itself will be returned.
	 * NO DEEP CLONING PROVIDED.
	 * @param {Object} cloneMe - Object to be cloned.
	 * @returns {Object} The Cloned object.
	 */
	static shallowClone (cloneMe) {
		if (Helper.isObject(cloneMe)) {
			return JSON.parse(JSON.stringify(cloneMe));
		}

		return cloneMe;
	}

	/**
	 * Merges given objects.
	 * @param {Object} mergeUs - Array of objects to be merged.
	 * @returns {Object} The merged Object.
	 */
	static mergeObjects (...mergeUs) {
		let merged = {};
		for (let mergeMe of mergeUs) {
			for (let key in mergeMe) {
				if (mergeMe.hasOwnProperty(key)) {
					merged[key] = mergeMe[key];
				}
			}
		}

		return merged;
	}

	/**
	 * Gets parameter from URL as a value.
	 * @param {string} param - Parameter to be read.
	 * @returns {string|null} Value of Parameter if parameter available. Null if parameter is not present. '' if parameter has no value.
	 */
	static getUrlParam (param) {
		let vars = {};
		('' + window.location.href).replace(/[?&]+([^=&]+)(?:=([^&]*))?/gi, function (m, key, value) {
			vars[decodeURIComponent(key)] = decodeURIComponent(value || '');
		});

		if (param in vars) {
			return vars[param];
		} else {
			return null;
		}
	}

	/**
	 * Returns parameter Object as an URL String.
	 * @param {Object} params - The Object containing the parameter keys and values.
	 * @returns {string} The URL string.
	 */
	static getParamsAsString (params) {
		const paramsArray = [];

		for (let key in params) {
			if (params.hasOwnProperty(key)) {
				if (typeof params[key] === 'function') {
					paramsArray.push(key + '=' + params[key]());
				} else {
					paramsArray.push(key + '=' + params[key]);
				}
			}
		}

		return paramsArray.join('&');
	}

	/**
	 * Returns element with given attribute.
	 * Calls itself recursively with parent element until element with attribute was found.
	 * @param element {Element} - Element to look for attribute.
	 * @param attributeName {string} - Attribute to return element for.
	 * @returns {undefined|Element} Returns undefined if given element and parents don't have given attribute name. Returns element if attribute found.
	 */
	static getElementByAttribute (element, attributeName) {
		if (!element) return;

		if (element === document.body) {
			return;
		}
		const matches = element.matches ? element.matches(attributeName) : element.msMatchesSelector(attributeName);
		if (matches) {
			return element;
		} else {
			return Helper.getElementByAttribute(element.parentElement, attributeName);
		}
	}

	/**
	 * Returns given attribute value.
	 * Calls itself recursively with parent element until element with attribute was found.
	 * @param element {Element} - Element to look for attribute.
	 * @param attributeName {string} - Attribute to return value for.
	 * @returns {undefined|string} Returns undefined if given element and parents don't have given attribute name. Returns value if found.
	 */
	static getAttributeValue (element, attributeName) {
		if (!element) return;

		if (element === document.body || element === document) {
			return;
		}

		if (element.hasAttribute(attributeName)) {
			return element.getAttribute(attributeName);
		} else {
			return Helper.getAttributeValue(element.parentNode, attributeName);
		}
	}

	/**
	 * Unchecks the checkbox inside a given element if the user clicked outside of the element scope
	 * @param {Event} event - The click event fired by caller
	 * @param {Element} checkboxParent - The element in whose scope the checkbox is unchecked
	 */
	static uncheckOnOutsideClick (event, checkboxParent) {
		let checkbox = checkboxParent.querySelector('input:checked');
		if (checkbox && !checkboxParent.contains(event.target)) {
			checkbox.checked = false;
		}
	}

	/**
	 * Creates a promise and starts XMLHttpRequest inside.
	 * @param {string} url The URL to call
	 * @param {'POST' | 'GET'} method The method to use for request
	 * @param {boolean} withCredentials Should credentials, e.g. cookies, be send with the request
	 * @returns {Promise<XMLHttpRequest>}
	 */
	static xhr (url, method = 'GET', withCredentials = true) {
		const request = new XMLHttpRequest();
		request.withCredentials = withCredentials;
		return new Promise((resolve, reject) => {
			request.onreadystatechange = function () {
				if (request.readyState !== 4) {
					return;
				}
				if (request.status >= 200 && request.status < 300) {
					resolve(request);
				} else {
					reject(request);
				}
			};
			request.open(method, url, true);
			request.send();
		});
	}

	/**
	 * Starts and returns a timer for the monitoring timeouts
	 * @param {object} timerObject the object with the timer data
	 * @returns {number|null} the timeout id or null if no timerObject given
	 */
	static getMonitoringTimer (timerObject) {
		if (!timerObject || isNaN(timerObject.delay) || new URLSearchParams(window.location.search).get('forwarding') === 'false') {
			return null;
		}

		return setTimeout(() => {
			window.location.href =
				'/?' +
				Object.keys(timerObject)
					.filter((k) => k !== 'delay')
					.map((k) => k + '=' + timerObject[k])
					.join('&');
		}, timerObject.delay);
	}

	/**
	 * Checks if stage is live stage.
	 * @returns {boolean} If stage is 'live'.
	 */
	static isLiveStage () {
		return window.PageConfig.stage === 'live' || window.PageConfig.stage === 'prelive';
	}

	/**
	 * Checks if element is visible. implicit also checks its parents elements for visibility.
	 * @param {Element} element - The element to check.
	 * @returns {boolean} If the object is visible.
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
	 */
	static isElementVisible (element) {
		return element.offsetParent !== null;
	}

	/**
	 * Reads first party cookie with given name.
	 * @param {string} cookieName - The Cookie name.
	 * @returns {string} The cookie Value. Empty if cookie not set.
	 */
	static getCookieValue (cookieName) {
		const findMe = '(?:^|; )' + cookieName + '=([^;]*)';
		return (document.cookie.match(findMe) || [0, ''])[1];
	}

	/**
	 * Builds a script tag and adds it to the head tag
	 * @param {string} scriptSrc - The script URL.
	 * @param {string} [callback] - A optional callback which runs after the script was loaded.
	 * @returns {string} The cookie Value. Empty if cookie not set.
	 */
	static addScript (scriptSrc) {
		return new Promise((resolve, reject) => {
			if (!scriptSrc) {
				reject(new Error('No script source'));
			}

			const head = document.getElementsByTagName('head')[0];
			const script = document.createElement('script');
			script.type = 'text/javascript';
			script.src = scriptSrc;

			script.onload = resolve;
			head.appendChild(script);
		});
	}

	/**
	 * Fires a custom event
	 * @param {string} eventName - The event name
	 * @param {object} detail - The detail object which comes with the event as payload
	 */
	static fireEvent (eventName, detail) {
		const bubbles = true;
		const cancelable = true;

		let event = document.createEvent('CustomEvent');
		event.initCustomEvent(
			eventName,
			bubbles,
			cancelable,
			detail
		);

		document.dispatchEvent(event);
	}

	/**
	 * checks if the element is overflowing it's parent element - so if it is cut off
	 * @param {DOMNode} element the DOM element which will be checked
	 * @returns {boolean}
	 */
	static isOverflowingParent (element) {
		const currentOverflow = element.style.overflow;

		if (!currentOverflow || currentOverflow === 'visible') {
			element.style.overflow = 'hidden';
		}

		const isOverflowing = element.clientWidth < element.scrollWidth || element.clientHeight < element.scrollHeight;
		element.style.overflow = currentOverflow;

		return isOverflowing;
	}
}

export { Helper };
